import type { OEPPhase } from './phases.js';
import type { OEPTimelineDateKey } from '../contracts/timeline.js';

export type OEPGateKey =
    | 'WelcomePresenters' | 'OpenDraftUpload' | 'CloseDraftUpload' | 'PublishMatches' | 'OpenReviews'
    | 'ReleaseFeedback' | 'OpenNominations' | 'CloseNominations' | 'AnnounceOutcomes';

export interface OEPGateDefinition {
    readonly Key: OEPGateKey;
    readonly From: OEPPhase;
    readonly To: OEPPhase;
    /** Staff-facing title (prototype opsActions, logic.js:619-626, where one exists). */
    readonly Title: string;
    /** What running it does — describes this platform's behavior, not the prototype's. */
    readonly Note: string;
    /** Timeline date shown beside the gate. */
    readonly DateKey: OEPTimelineDateKey;
}

export const OEP_GATES: readonly OEPGateDefinition[] = [
    { Key: 'WelcomePresenters', From: 'Setup', To: 'Accepted', Title: 'Close presenter application', Note: 'Sends the welcome email to every enrolled presenter.', DateKey: 'ApplicationsClose' },
    { Key: 'OpenDraftUpload', From: 'Accepted', To: 'DraftUpload', Title: 'Open draft upload', Note: 'Presenters can upload and replace their draft and set their career stage.', DateKey: 'UploadOpens' },
    { Key: 'CloseDraftUpload', From: 'DraftUpload', To: 'DraftClosed', Title: 'Close draft upload', Note: 'Locks drafts. Load reviewer assignments for the presentations that have a draft, then run Match reviewers to presenters.', DateKey: 'DraftDue' },
    { Key: 'PublishMatches', From: 'DraftClosed', To: 'Matched', Title: 'Match reviewers to presenters', Note: 'Cancels assignments whose presentation has no draft, then makes the rest visible to reviewers and emails each matched reviewer.', DateKey: 'MatchingRun' },
    { Key: 'OpenReviews', From: 'Matched', To: 'Reviewing', Title: 'Open the feedback window', Note: 'Reviewers can score and comment; each reviewer is emailed.', DateKey: 'ReviewWindow' },
    { Key: 'ReleaseFeedback', From: 'Reviewing', To: 'FeedbackReleased', Title: 'Release feedback to presenters', Note: 'Also prompts presenters to thank their reviewers.', DateKey: 'FeedbackRelease' },
    { Key: 'OpenNominations', From: 'FeedbackReleased', To: 'Nominating', Title: 'Open nominations', Note: 'Session nominators can nominate; each is emailed.', DateKey: 'NominatorAccess' },
    { Key: 'CloseNominations', From: 'Nominating', To: 'Selecting', Title: 'Close nominations, open committee view', Note: 'Committees get 3 weeks to select winners.', DateKey: 'NominationsClose' },
    { Key: 'AnnounceOutcomes', From: 'Selecting', To: 'Complete', Title: 'Announce outcomes', Note: 'Locks slates and emails winners, other presenters and volunteers.', DateKey: 'WinnersAnnounced' },
];

export class GateOrderError extends Error {
    constructor(public readonly Gate: OEPGateKey, public readonly Phase: OEPPhase) {
        super(`Gate "${Gate}" cannot run while the season is in phase "${Phase}".`);
        this.name = 'GateOrderError';
    }
}

export function GateByKey(key: OEPGateKey): OEPGateDefinition {
    const gate = OEP_GATES.find((g) => g.Key === key);
    if (!gate) throw new Error(`Unknown gate ${key}`);
    return gate;
}

export function NextGate(phase: OEPPhase): OEPGateDefinition | null {
    return OEP_GATES.find((g) => g.From === phase) ?? null;
}

/** Throws GateOrderError unless `gate` is the next gate for `phase` (so a rerun always fails). */
export function AssertGateRunnable(gate: OEPGateKey, phase: OEPPhase): OEPGateDefinition {
    const def = GateByKey(gate);
    if (def.From !== phase) throw new GateOrderError(gate, phase);
    return def;
}
